import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Settings, Save, X, FileText, Calendar, AlertCircle, CheckCircle } from 'lucide-react'
import { Modal, Button, Input } from '@components/common'
import taskService from '@services/taskService'
import './TaskConfigurationModal.scss'

/**
 * TaskConfigurationModal Component
 *
 * Allows CM/PM to configure task details:
 * - Edit title and description
 * - Set evidence requirement
 * - Set due date
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Modal visibility state
 * @param {Function} props.onClose - Close handler
 * @param {Object} props.task - Task data to configure
 * @param {Function} props.onSave - Save callback with updated task
 */
const TaskConfigurationModal = ({ isOpen, onClose, task, onSave }) => {
	const [formData, setFormData] = useState({
		title: '',
		description: '',
		evidence_required: false,
		due_date: '',
	})
	const [loading, setLoading] = useState(false)
	const [error, setError] = useState(null)
	const [success, setSuccess] = useState(false)

	// Initialize form with task data
	useEffect(() => {
		if (task) {
			setFormData({
				title: task.title || '',
				description: task.description || '',
				evidence_required: task.evidence_required == 1 || task.evidence_required === true || task.evidence_required === '1',
				due_date: task.due_date ? task.due_date.split(' ')[0] : '',
			})
			setError(null)
			setSuccess(false)
		}
	}, [task])

	const handleChange = (e) => {
		const { name, value, type, checked } = e.target
		setFormData((prev) => ({
			...prev,
			[name]: type === 'checkbox' ? checked : value,
		}))
	}

	const handleSubmit = async (e) => {
		e.preventDefault()
		setLoading(true)
		setError(null)
		setSuccess(false)

		// Validate due date is set
		if (!formData.due_date) {
			setError('Due date is required. Please set a due date for this task.')
			setLoading(false)
			return
		}

		try {
			const response = await taskService.configureTask(task.id, {
				title: formData.title.trim(),
				description: formData.description.trim(),
				evidence_required: formData.evidence_required,
				due_date: formData.due_date,
			})

			if (response.success) {
				setSuccess(true)
				if (onSave) {
					onSave(response.data)
				}
				// Close modal after a brief delay to show success
				setTimeout(() => {
					onClose()
				}, 1000)
			} else {
				setError(response.message || 'Failed to save task configuration')
			}
		} catch (err) {
			console.error('Error configuring task:', err)
			setError(err.message || 'Failed to save task configuration')
		} finally {
			setLoading(false)
		}
	}

	const handleClose = () => {
		if (!loading) {
			setError(null)
			setSuccess(false)
			onClose()
		}
	}

	if (!task) return null

	return (
		<Modal
			isOpen={isOpen}
			onClose={handleClose}
			title={
				<span className="task-config-modal__title">
					<Settings size={20} />
					Configure Task
				</span>
			}
			size="medium"
			closeOnBackdrop={false}
		>
			<form onSubmit={handleSubmit} className="task-config-modal">
				<div className="task-config-modal__content">
					{error && (
						<div className="task-config-modal__error">
							<AlertCircle size={16} />
							{error}
						</div>
					)}

					{success && (
						<div className="task-config-modal__success">
							<CheckCircle size={16} />
							Task configured successfully!
						</div>
					)}

					<div className="task-config-modal__field">
						<label htmlFor="title">
							<FileText size={16} />
							Task Title
						</label>
						<Input
							id="title"
							name="title"
							value={formData.title}
							onChange={handleChange}
							placeholder="Enter task title"
							required
							disabled={loading}
						/>
					</div>

					<div className="task-config-modal__field">
						<label htmlFor="description">
							<FileText size={16} />
							Description
						</label>
						<textarea
							id="description"
							name="description"
							value={formData.description}
							onChange={handleChange}
							placeholder="Enter task description (optional)"
							rows={4}
							disabled={loading}
						/>
					</div>

					<div className="task-config-modal__field task-config-modal__field--required">
						<label htmlFor="due_date">
							<Calendar size={16} />
							Due Date <span className="required-indicator">*</span>
						</label>
						<Input
							id="due_date"
							name="due_date"
							type="date"
							value={formData.due_date}
							onChange={handleChange}
							disabled={loading}
							required
						/>
						{!formData.due_date && (
							<small className="field-hint field-hint--warning">
								<AlertCircle size={12} />
								Due date is required for task tracking
							</small>
						)}
					</div>

					<div className="task-config-modal__field task-config-modal__field--checkbox">
						<label className="checkbox-label">
							<input
								type="checkbox"
								name="evidence_required"
								checked={formData.evidence_required}
								onChange={handleChange}
								disabled={loading}
							/>
							<span className="checkbox-custom"></span>
							<span className="checkbox-text">
								<strong>Evidence Required</strong>
								<small>User must upload evidence documents to complete this task</small>
							</span>
						</label>
					</div>
				</div>

				<div className="task-config-modal__actions">
					<Button type="button" variant="secondary" onClick={handleClose} disabled={loading}>
						<X size={16} />
						Cancel
					</Button>
					<Button type="submit" variant="primary" disabled={loading}>
						{loading ? (
							<>
								<span className="spinner-small"></span>
								Saving...
							</>
						) : (
							<>
								<Save size={16} />
								Save Configuration
							</>
						)}
					</Button>
				</div>
			</form>
		</Modal>
	)
}

TaskConfigurationModal.propTypes = {
	isOpen: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	task: PropTypes.shape({
		id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
		title: PropTypes.string,
		description: PropTypes.string,
		evidence_required: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
		due_date: PropTypes.string,
	}),
	onSave: PropTypes.func,
}

TaskConfigurationModal.defaultProps = {
	task: null,
	onSave: null,
}

export default TaskConfigurationModal
